import { forwardRef, useCallback, useEffect, useId, useRef, useState, type HTMLAttributes, type ReactNode, type RefObject } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from '../../icons/Icon';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useMergedRefs } from '../Tooltip/mergeRefs';
import { Button } from '../Button/Button';
import { ModalOverlay } from '../Dialog/ModalOverlay';
import { useDsT } from '../../services/i18n';

/** `full` is the pre-Modal spelling of `fullscreen` and stays accepted. */
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen' | 'full';

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** One line under the title. */
  subtitle?: ReactNode;
  /** @deprecated use `subtitle`. */
  description?: ReactNode;
  /** Sticky footer slot (actions). */
  footer?: ReactNode;
  /** 440 / 600 / 840 / 1120 px max-width from the `--zo-modal-*` tokens, or the whole viewport. */
  size?: ModalSize;
  closeOnEscape?: boolean;
  closeOnOverlayClick?: boolean;
  /** Element to focus when the modal opens (defaults to the first focusable). */
  initialFocusRef?: RefObject<HTMLElement | null>;
  hideCloseButton?: boolean;
  /** Body scrolls inside the modal when content overflows (default true). */
  scrollBody?: boolean;
  /**
   * The modal holds unsaved work: Escape, the backdrop and the close button ask before discarding it.
   * `onClose` is only called once the operator confirms.
   */
  dirty?: boolean;
  /** Label of the button that discards the unsaved work. */
  confirmCloseLabel?: string;
  /** Label of the button that keeps the modal open. */
  keepEditingLabel?: string;
  /** Sentence shown while asking. */
  confirmCloseMessage?: ReactNode;
  /** Accessible name when no title is rendered. */
  'aria-label'?: string;
  children?: ReactNode;
}

/** Width classes per size, from the `--container-modal-*` theme mapping of the `--zo-modal-*` tokens. */
export const modalSizes: Record<ModalSize, string> = {
  sm: 'max-w-modal-sm',
  md: 'max-w-modal-md',
  lg: 'max-w-modal-lg',
  xl: 'max-w-modal-xl',
  fullscreen: 'max-w-none h-full w-full rounded-none',
  full: 'max-w-none h-full w-full rounded-none',
};

const closeButtonClass =
  'inline-flex size-7 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors motion-fast hover:bg-hover hover:text-fg focus-ring';

/**
 * Modal dialog: portalled over the dimmed (never blurred) `--zo-surface-overlay` backdrop, focus-trapped,
 * scroll-locked, and mounted through `usePresence` so it animates out instead of vanishing. Header with a
 * close button, a body that scrolls on its own, a sticky footer, and a dirty guard that asks before an
 * Escape or a backdrop click throws unsaved work away. Separation comes from elevation and the surface step,
 * not from borders.
 */
export const Modal = forwardRef<HTMLDivElement, ModalProps>(function Modal(
  {
    open,
    onClose,
    title,
    subtitle,
    description,
    footer,
    size = 'md',
    closeOnEscape = true,
    closeOnOverlayClick = true,
    initialFocusRef,
    hideCloseButton,
    scrollBody = true,
    dirty = false,
    confirmCloseLabel: confirmCloseLabelProp,
    keepEditingLabel: keepEditingLabelProp,
    confirmCloseMessage: confirmCloseMessageProp,
    'aria-label': ariaLabel,
    className,
    children,
    ...rest
  },
  ref,
) {
  const id = useId();
  const t = useDsT();
  const confirmCloseLabel = confirmCloseLabelProp === undefined ? t('ds.common.discardChanges', 'Discard changes') : confirmCloseLabelProp;
  const keepEditingLabel = keepEditingLabelProp === undefined ? t('ds.common.keepEditing', 'Keep editing') : keepEditingLabelProp;
  const confirmCloseMessage = confirmCloseMessageProp === undefined ? t('ds.common.confirmDiscard', 'You have unsaved changes. Discard them?') : confirmCloseMessageProp;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const keepRef = useRef<HTMLButtonElement | null>(null);
  // The panel mounts with the overlay's presence, which can lag `open` by a commit.
  const [panelNode, setPanelNode] = useState<HTMLDivElement | null>(null);
  const [confirming, setConfirming] = useState(false);
  const sub = subtitle ?? description;
  const fullscreen = size === 'fullscreen' || size === 'full';

  /** Every close path goes through here so the dirty guard cannot be bypassed. */
  const requestClose = useCallback(() => {
    if (dirty) {
      setConfirming(true);
      return;
    }
    onClose();
  }, [dirty, onClose]);
  const onEscape = useCallback(() => {
    if (confirming) setConfirming(false);
    else requestClose();
  }, [confirming, requestClose]);

  useEscapeKey(onEscape, open && closeOnEscape);
  useFocusTrap(panelRef, open && panelNode != null, { initialFocus: !initialFocusRef, restoreFocus: true });
  useEffect(() => {
    if (!open) {
      setPanelNode(null);
      setConfirming(false);
    }
  }, [open]);
  useEffect(() => {
    if (!dirty) setConfirming(false);
  }, [dirty]);
  useEffect(() => {
    if (open && panelNode && initialFocusRef?.current) queueMicrotask(() => initialFocusRef.current?.focus({ preventScroll: true }));
  }, [open, panelNode, initialFocusRef]);
  useEffect(() => {
    if (confirming) queueMicrotask(() => keepRef.current?.focus({ preventScroll: true }));
  }, [confirming]);

  const attachPanel = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
    setPanelNode(node);
  }, []);
  const setPanel = useMergedRefs<HTMLDivElement>(attachPanel, ref);

  return (
    <ModalOverlay
      open={open}
      onDismiss={requestClose}
      closeOnOverlayClick={closeOnOverlayClick}
      data-dialog-overlay=""
      data-modal-overlay=""
      className={cn('zo-modal-overlay zo-dialog-overlay items-center justify-center', fullscreen ? 'p-0' : 'p-4')}
    >
      {(state) => (
        <div
          ref={setPanel}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title != null ? `${id}-title` : undefined}
          aria-label={title == null ? ariaLabel ?? t('ds.modal.label', 'Dialog') : undefined}
          aria-describedby={sub != null ? `${id}-desc` : undefined}
          tabIndex={-1}
          data-state={state}
          data-size={size}
          data-dirty={dirty || undefined}
          className={cn(
            'zo-floating zo-modal zo-dialog flex max-h-full w-full flex-col overflow-hidden rounded-xl bg-surface-3 text-fg shadow-4 outline-none',
            modalSizes[size],
            className,
          )}
          {...rest}
        >
          {title != null || !hideCloseButton ? (
            <div className="flex shrink-0 items-start gap-3 px-5 pb-2 pt-4">
              <div className="min-w-0 flex-1">
                {title != null ? (
                  <h2 id={`${id}-title`} className="type-title">
                    {title}
                  </h2>
                ) : null}
                {sub != null ? (
                  <p id={`${id}-desc`} className="mt-1 type-body text-fg-muted">
                    {sub}
                  </p>
                ) : null}
              </div>
              {!hideCloseButton ? (
                <button type="button" aria-label={t('ds.common.close', 'Close')} onClick={requestClose} className={cn('-me-2 -mt-1', closeButtonClass)}>
                  <Icon name="x" size={16} />
                </button>
              ) : null}
            </div>
          ) : null}
          <div className={cn('min-h-0 flex-1 px-5 py-3', scrollBody && 'overflow-auto scrollbar-thin')}>{children}</div>
          {confirming ? (
            <div
              role="alertdialog"
              aria-label={typeof confirmCloseMessage === 'string' ? confirmCloseMessage : t('ds.common.discardUnsaved', 'Discard unsaved changes?')}
              data-modal-confirm-close=""
              className="flex shrink-0 flex-wrap items-center gap-3 border-t border-warning-border bg-warning-bg px-5 py-3 type-body"
            >
              <Icon name="alert" size={16} className="shrink-0 text-warning-fg" />
              <span className="min-w-0 flex-1 text-fg">{confirmCloseMessage}</span>
              <div className="flex shrink-0 items-center gap-2">
                <Button ref={keepRef} size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                  {keepEditingLabel}
                </Button>
                <Button size="sm" variant="danger" onClick={onClose}>
                  {confirmCloseLabel}
                </Button>
              </div>
            </div>
          ) : null}
          {footer != null ? (
            <div className="sticky bottom-0 flex shrink-0 items-center justify-end gap-2 border-t border-border-subtle bg-surface-3 px-5 py-3">{footer}</div>
          ) : (
            <div className="h-2 shrink-0" />
          )}
        </div>
      )}
    </ModalOverlay>
  );
});
